export const ConverterType = {
	recursive: "recursive",
	array: "array",
	baseExcludeNode: "baseExcludeNode",
	excludeNode: "excludeNode",
	node: "node",
} as const;

type TConverterType = (typeof ConverterType)[keyof typeof ConverterType];

/**
 *
 * @description 階層を問わない ViewModel へ変換する際に拡張しないプロパティ名
 *
 * @see {ExcludePropUnionType}
 */
const BaseExcludeKeys: Array<string> = ["id", "__typename"];

const converter = (_obj: unknown, _excludeKeys: Array<string>, _type: TConverterType): unknown => {
	// 拡張しないプロパティの場合は、そのまま返す
	if (_type === "excludeNode") {
		return _obj;
	}

	// プリミティブな値の場合は、拡張して終了
	if (_type === "node") {
		return {
			value: _obj,
			isEdited: false,
			error: undefined,
		};
	}

	if (_type === "array") {
		const values = (_obj as Array<unknown>).map((value) => {
			// @todo: type の代入を汎用化する
			//値がオブジェクトの場合は、_type = recursive を指定して再帰
			if (typeof value === "object") return converter(value, [], ConverterType.recursive);

			// 値が配列の場合は、_type = array を指定して再帰
			if (Array.isArray(value)) return converter(value, [], ConverterType.array);

			// 配列の値がプリミティブな場合は、_type = node を指定して再帰しない
			return converter(value, [], ConverterType.node);
		});

		//
		return {
			value: values,
			isEdited: false,
			hasError: false,
		};
	}

	// ViewModel
	const obj: { [key: string]: unknown } = {};

	/**
	 *
	 * @example
	 * ```
	 * const object1 = {
	 *   a: 'somestring',
	 *   b: 42,
	 * };
	 * for (const [key, value] of Object.entries(object1)) {
	 *  console.log(`${key}: ${value}`);
	 * }
	 *
	 * Expected output:
	 *   "a: somestring"
	 *   "b: 42"
	 * ```
	 */
	for (const [key, value] of Object.entries(_obj as object)) {
		// 拡張しないプロパティなので再帰しない
		if (BaseExcludeKeys.includes(key) || _excludeKeys.includes(key)) {
			obj[key] = converter(value, [], ConverterType.excludeNode);
			continue;
		}

		// 値が配列の場合は、_type = array を指定して再帰
		if (Array.isArray(value)) {
			obj[key] = converter(value, [], ConverterType.array);
			continue;
		}

		// 値がオブジェクトの場合は、_type = recursive を指定して再帰
		// typeof null === 'object' なので、null は除外する
		if (value !== null && typeof value === "object") {
			obj[key] = converter(value, [], ConverterType.recursive);
			continue;
		}

		//  null または undefined の場合は、_type = excludeNode を指定して再帰しない
		if (value === null || value === undefined) {
			obj[key] = converter(value, [], ConverterType.excludeNode);
			continue;
		}

		// 値がプリミティブな場合は、_type = node を指定して再帰しない
		obj[key] = converter(value, [], ConverterType.node);
	}

	// InputModel から ViewModel の型へマッピングした ViewModel を返す
	return obj;
};

/**
 *
 * @description InputModel から ViewModel へのマッピングを行う再帰関数
 *
 * @param _obj - AutoGeneratorForFormModel<I, E, Q>["InputModel"]
 * @returns ViewModel - AutoGeneratorForFormModel<I, E, Q>["ViewModel"]
 */
export const viewModelConverter = (_obj: unknown, _excludeKeys: Array<string>): { [key: string]: unknown } => {
	const converted = converter(_obj, _excludeKeys, ConverterType.recursive) as { [key: string]: unknown };
	converted["isDirty"] = false;
	converted["hasError"] = false;
	return converted;
};
